import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AvailabilityResponse,
  DepartmentListResponse,
  PlannerPerspectiveResponse,
  ResourceListResponse,
  ResourceSearchResponse,
  RoleListResponse,
  RpProjectListResponse,
  UtilisationGranularity,
  UtilisationMode,
} from '@timelog/pm-shared';

import { QueryKeys } from './queryKeys';
import { useApiClient } from './useApiClient';

/**
 * Hooks for the ResourceAxis surface (`/api/rp/*`). Kept out of hooks.ts so
 * the resource-planning surface can grow without touching the local-project
 * hook file (the timelogHooks.ts precedent).
 */

/** Org catalogs change rarely — cache like the lookup catalogs. */
const CATALOG_STALE_MS = 5 * 60_000;

export const useRpDepartments = () => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.RpDepartments,
    queryFn: () => api.get<DepartmentListResponse>('/rp/departments'),
    staleTime: CATALOG_STALE_MS,
  });
};

export const useRpRoles = () => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.RpRoles,
    queryFn: () => api.get<RoleListResponse>('/rp/roles'),
    staleTime: CATALOG_STALE_MS,
  });
};

export const useRpResources = () => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.RpResources,
    queryFn: () => api.get<ResourceListResponse>('/rp/resources'),
    staleTime: CATALOG_STALE_MS,
  });
};

export const useRpProjects = (enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.RpProjects,
    queryFn: () => api.get<RpProjectListResponse>('/rp/projects'),
    staleTime: 60_000,
    enabled,
  });
};

export type PlannerPerspectiveParams = {
  from: string;
  to: string;
  granularity: UtilisationGranularity;
  mode: UtilisationMode;
};

/**
 * The planner page's single data read. `keepPreviousData` so scale/mode
 * toggles re-render over the old grid instead of blanking it.
 */
export const usePlannerPerspective = (params: PlannerPerspectiveParams) => {
  const api = useApiClient();
  const { from, to, granularity, mode } = params;
  return useQuery({
    queryKey: QueryKeys.RpPlanner(from, to, granularity, mode),
    queryFn: () =>
      api.get<PlannerPerspectiveResponse>(
        `/rp/perspectives/planner?from=${from}&to=${to}&granularity=${granularity}&mode=${mode}`,
      ),
    placeholderData: keepPreviousData,
  });
};

/** Capacity vs booked over a free range — Find & Allocate's ranking input. */
export const useRpAvailability = (from: string, to: string, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.RpAvailability(from, to),
    queryFn: () => api.get<AvailabilityResponse>(`/rp/availability?from=${from}&to=${to}`),
    placeholderData: keepPreviousData,
    enabled,
  });
};

/** Server-side resource search (Typesense-enriched in live lookup mode). */
export const useRpResourceSearch = (q: string, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.RpResourceSearch(q),
    queryFn: () =>
      api.get<ResourceSearchResponse>(`/rp/resources/search?q=${encodeURIComponent(q)}&limit=20`),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
    enabled,
  });
};

export type ResolveGenericInput = {
  allocationId: string;
  resourceId: string;
  hoursToResolve: number;
};

/** Move hours: resolve (part of) a generic slot to a named person (DL-031). */
export const useResolveGeneric = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ResolveGenericInput) =>
      api.post<unknown>(`/rp/allocations/${input.allocationId}/resolve`, {
        resourceId: input.resourceId,
        hoursToResolve: input.hoursToResolve,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rp', 'planner'] });
      void queryClient.invalidateQueries({ queryKey: ['rp', 'availability'] });
    },
  });
};

export type AllocateWithBookingInput = {
  workItemId: string;
  resourceKind: 'NAMED' | 'GENERIC';
  /** NAMED only. */
  resourceId?: string;
  /** GENERIC only. */
  roleId?: string;
  hours: number;
  startDate: string;
  endDate: string;
};

/**
 * Find & Allocate's commit: create the allocation on the project's work item,
 * then spread the hours over the range with a period booking (PROPORTIONAL —
 * the backend distributes to capacity-weighted days and owns the maths).
 */
export const useAllocateWithBooking = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AllocateWithBookingInput) => {
      const created = await api.post<{ id: string }>('/rp/allocations', {
        workItemId: input.workItemId,
        resourceKind: input.resourceKind,
        ...(input.resourceKind === 'NAMED'
          ? { resourceId: input.resourceId }
          : { roleId: input.roleId }),
        allocatedBudget: input.hours,
      });
      await api.put<unknown>(`/rp/allocations/${created.id}/bookings`, {
        startDate: input.startDate,
        endDate: input.endDate,
        totalHours: input.hours,
        strategy: 'PROPORTIONAL',
        granularity: 0.25,
      });
      return created;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rp', 'planner'] });
      void queryClient.invalidateQueries({ queryKey: ['rp', 'availability'] });
    },
  });
};
